export function assertNever(_: never): never {
    throw new Error(`Value should be never: ${_}`);
}

// NO-OP. Typescript handles that
export const isInterchangeable = <TFrom extends TTo, TTo>(element?: TFrom): TTo | undefined => element;

// Covers *most* of the cases because `NaN` cannot be added to the union.
export type Falsy = false | 0 | '' | null | undefined;

export type StrictOmit<TObject, TKey extends keyof TObject> = Pick<TObject, Exclude<keyof TObject, TKey>>;

// It will also correctly work for discriminated unions.
export type UnionAwareOmit<TObject, TKey extends keyof TObject> = {
    [TProperty in keyof TObject as TProperty extends TKey ? never : TProperty]: TObject[TProperty];
};

export type StrictExtract<TObject, TKey extends TObject> = TKey extends TObject ? TKey : never;

export type IdType = string | number;

export type ReactKey = string | number;

export type WithId<TObject extends object> = TObject & { id: string };

export type ArrayElement<TArrayType extends readonly unknown[]> = TArrayType extends readonly (infer TElementType)[]
    ? TElementType
    : never;

export type WithRequired<TObject, TKey extends keyof TObject> = TObject & { [TProp in TKey]-?: TObject[TProp] };

export type WithOptional<TObject, TKey extends keyof TObject> = Omit<TObject, TKey> & {
    [TProp in TKey]?: TObject[TProp];
};

export type WithNonNullable<TObject, TKey extends keyof TObject> = TObject & {
    [TProp in TKey]: NonNullable<TObject[TProp]>;
};

type NonSymbol<TKey> = Exclude<TKey, symbol>;

export type Paths<TObject> = TObject extends object
    ? { [TKey in keyof TObject]-?: `${NonSymbol<TKey>}${'' | `.${Paths<TObject[TKey]>}`}` }[keyof TObject]
    : never;

export type WithNonNullableProperties<TObject extends object> = {
    [TKey in keyof TObject]: NonNullable<TObject[TKey]>;
};

type Join<TFirst extends PropertyKey, TSecond extends PropertyKey> = `${NonSymbol<TFirst>}.${NonSymbol<TSecond>}`;

type SimplifyForFlattening<TObject extends object> = Required<Omit<TObject, 'children'>>;

type NestLimit = 4;

type Incr = [1, 2, 3, NestLimit];

type FlattenInternal<
    TObject extends object,
    TLevel extends number = 0,
    TKeys extends keyof TObject = keyof TObject,
> = TLevel extends NestLimit
    ? { [TProperty in TKeys]: TObject[TKeys] }
    : TKeys extends string
      ? TObject[TKeys] extends object
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- we actually need `Function` here
            TObject[TKeys] extends Function | Array<any> // Non flattenable for now
              ? { [TProperty in TKeys]: TObject[TKeys] }
              : FlattenInternal<SimplifyForFlattening<TObject[TKeys]>, Incr[TLevel]> extends infer TInferred
                ? {
                      [TProperty in keyof TInferred as Join<TKeys, TProperty>]: TInferred[TProperty];
                  }
                : { [TProperty in TKeys]: TObject[TKeys] }
          : { [TProperty in TKeys]: TObject[TKeys] }
      : never;

export type Flatten<TObject extends object> = FlattenInternal<SimplifyForFlattening<TObject>>;

export type CommonProps<TObject1 extends object, TObject2 extends object> = Pick<
    TObject1 | TObject2,
    Extract<keyof TObject1, keyof TObject2>
>;

export type Writeable<T> = { -readonly [P in keyof T]: T[P] };
